import React from 'react'
import { createContext } from 'lib/createContext'
import { Loading, Message } from '@workwhile/ui'
import { parseErrorMessage } from 'api'
import { useIndeedFlexShiftIssuesPaginationQueryState } from '../hooks/useIndeedFlexShiftIssuesPaginationQueryState'
import { useIndeedFlexShiftIssuesQuery } from 'queries/admin/indeedFlex/useIndeedFlexShiftIssuesQuery'
import { useIndeedFlexIssuesFiltersQueryState } from 'pages/IndeedFlex/shared/hooks/useIndeedFlexIssuesFiltersQueryState'

type IndeedFlexShiftIssuesContextState = {
  state: {
    data: NonNullable<ReturnType<typeof useIndeedFlexShiftIssuesQuery>['data']>
    dataUpdatedAt: number
    isFetching: boolean
    isError: boolean
    error: string
    pagination: {
      pageIndex: number
      pageSize: number
    }
  }
  actions: {
    refetch: () => void
    setPagination: ReturnType<
      typeof useIndeedFlexShiftIssuesPaginationQueryState
    >[1]
  }
}

const [Context, useIndeedFlexShiftIssuesContextValue] =
  createContext<IndeedFlexShiftIssuesContextState>({
    name: 'IndeedFlexShiftIssuesContext',
  })

export const IndeedFlexShiftIssuesProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [filters] = useIndeedFlexIssuesFiltersQueryState()

  const [pagination, setPagination] =
    useIndeedFlexShiftIssuesPaginationQueryState()

  const {
    data,
    isPending,
    isError,
    error,
    dataUpdatedAt,
    refetch,
    isFetching,
  } = useIndeedFlexShiftIssuesQuery({
    page: pagination.pageIndex,
    pageSize: pagination.pageSize,
    daysAgo: filters.daysAgo,
    ifClientIds: filters.ifClientIds,
    companyIds: filters.companyIds,
    issues: filters.issues,
  })

  if (isPending) {
    return <Loading />
  }

  if (isError && !data) {
    // eslint-disable-next-line no-console
    console.error(error)
    return (
      <Message variant="error" m={4} borderRadius="small">
        Sorry, something went wrong. Please reach out to eng support with the
        URL of the page if this persists.
      </Message>
    )
  }

  if (!data) {
    return null
  }

  const value: IndeedFlexShiftIssuesContextState = {
    state: {
      data,
      dataUpdatedAt,
      isFetching,
      isError,
      error: parseErrorMessage(error),
      pagination,
    },
    actions: {
      refetch,
      setPagination,
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { useIndeedFlexShiftIssuesContextValue }
